import React, { useMemo } from 'react'
import { SingleValue } from 'react-select'
import { AppSelect } from '../../../../../../components/Select'
import { MODE_LABEL, MODES, PAYOUT_KIND_FILTER_LABEL, PAYOUT_KIND_FILTERS } from '../core/consts'
import { TimelineResponse } from '../core/response.types'
import { PayoutKindFilter, TimelineView } from '../core/types'
import { sideOptions } from '../core/view'

type IdOption = { value: number; label: string }
type KindOption = { value: PayoutKindFilter; label: string }
type StringOption = { value: string; label: string }

type Props = {
  data: TimelineResponse | null
  currency: string
  onCurrency: (currency: string) => void
  view: TimelineView
  onView: (view: TimelineView) => void
}

const KIND_OPTIONS: KindOption[] = PAYOUT_KIND_FILTERS.map((k) => ({
  value: k,
  label: PAYOUT_KIND_FILTER_LABEL[k],
}))

/**
 * Выбранный id, которого в новом окне нет, остаётся в списке с id вместо
 * названия — выбор не пропадает из-под руки при сдвиге окна.
 */
const idOptions = (dictionary: { id: number; name: string }[], selected: number[]): IdOption[] => {
  const known = new Map(dictionary.map((d) => [d.id, d.name]))
  const ids = Array.from(new Set([...dictionary.map((d) => d.id), ...selected]))
  return ids
    .map((id) => ({ value: id, label: known.get(id) ?? `#${id}` }))
    .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
}

const Field: React.FC<{ label: string; width?: number; children: React.ReactNode }> = ({
  label,
  width,
  children,
}) => (
  <div className='d-flex flex-column gap-1' style={width ? { minWidth: width } : undefined}>
    <span className='text-muted fs-8 fw-semibold'>{label}</span>
    {children}
  </div>
)

type IdMultiProps = {
  label: string
  options: IdOption[]
  value: number[]
  onChange: (ids: number[]) => void
  placeholder: string
}

const IdMulti: React.FC<IdMultiProps> = ({ label, options, value, onChange, placeholder }) => (
  <Field label={label} width={240}>
    <AppSelect<IdOption, true>
      isMulti
      options={options}
      value={options.filter((o) => value.includes(o.value))}
      onChange={(opts) => onChange(opts.map((o) => o.value))}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      isClearable
    />
  </Field>
)

const TimelineHeader: React.FC<Props> = ({ data, currency, onCurrency, view, onView }) => {
  const payoutSide = useMemo(() => sideOptions(data, 'payouts'), [data])
  const depositSide = useMemo(() => sideOptions(data, 'deposits'), [data])
  const currencyOptions: StringOption[] = (
    data?.currencies?.length ? data.currencies : [currency]
  ).map((c) => ({
    value: c,
    label: c,
  }))
  const merchantDict = (list: { id: number; title: string }[]) =>
    list.map((m) => ({ id: m.id, name: m.title || `#${m.id}` }))

  return (
    <div className='card mb-4'>
      <div className='card-body py-4 d-flex flex-column gap-4'>
        <div className='d-flex flex-wrap align-items-end gap-4'>
          <Field label='Валюта' width={100}>
            <AppSelect<StringOption, false>
              options={currencyOptions}
              value={currencyOptions.find((o) => o.value === currency)}
              onChange={(opt: SingleValue<StringOption>) => opt && onCurrency(opt.value)}
              isSearchable={false}
            />
          </Field>

          <Field label='Режим'>
            <div className='btn-group' role='group' aria-label='Режим'>
              {MODES.map((m) => (
                <button
                  key={m}
                  type='button'
                  className={`btn btn-sm ${view.mode === m ? 'btn-primary' : 'btn-light'}`}
                  onClick={() => onView({ ...view, mode: m })}
                >
                  {MODE_LABEL[m]}
                </button>
              ))}
            </div>
          </Field>

          {view.mode === 'all' && (
            <Field label='Виды выплат' width={300}>
              <AppSelect<KindOption, true>
                isMulti
                options={KIND_OPTIONS}
                value={KIND_OPTIONS.filter((o) => view.payoutKinds.includes(o.value))}
                onChange={(opts) => onView({ ...view, payoutKinds: opts.map((o) => o.value) })}
                placeholder='Ни одного вида'
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
              />
            </Field>
          )}
        </div>

        <div className='d-flex flex-wrap gap-8'>
          <div className='d-flex flex-column gap-2'>
            <span className='fw-bold fs-7'>Выплаты</span>
            <div className='d-flex flex-wrap gap-4'>
              <IdMulti
                label='Мерчанты'
                options={idOptions(merchantDict(payoutSide.merchants), view.payoutMerchantIds)}
                value={view.payoutMerchantIds}
                onChange={(ids) => onView({ ...view, payoutMerchantIds: ids })}
                placeholder='Все мерчанты'
              />
              <IdMulti
                label='Платёжные методы'
                options={idOptions(payoutSide.methods, view.payoutMethodIds)}
                value={view.payoutMethodIds}
                onChange={(ids) => onView({ ...view, payoutMethodIds: ids })}
                placeholder='Все методы'
              />
            </div>
          </div>

          <div className='d-flex flex-column gap-2'>
            <span className='fw-bold fs-7'>Депозиты</span>
            <div className='d-flex flex-wrap gap-4'>
              <IdMulti
                label='Мерчанты'
                options={idOptions(merchantDict(depositSide.merchants), view.depositMerchantIds)}
                value={view.depositMerchantIds}
                onChange={(ids) => onView({ ...view, depositMerchantIds: ids })}
                placeholder='Все мерчанты'
              />
              <IdMulti
                label='Платёжные методы'
                options={idOptions(depositSide.methods, view.depositMethodIds)}
                value={view.depositMethodIds}
                onChange={(ids) => onView({ ...view, depositMethodIds: ids })}
                placeholder={
                  view.payoutMethodIds.length > 0 ? 'Совместимые с методами выплат' : 'Все методы'
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TimelineHeader
